import { useEffect, useRef, useState } from "react";
import { Upload, Link2, Link2Off, EyeOff, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { EmptyRow, Money, NativeSelect, PageHeader, Panel } from "@/components/common";
import { Button } from "@/components/ui/button";

const BADGE = { matched: "bg-emerald-50 text-emerald-800 ring-emerald-200", unmatched: "bg-amber-50 text-amber-800 ring-amber-200", ignored: "bg-slate-100 text-slate-500 ring-slate-200" };

function MatchPicker({ tx, invoices, expenses, onMatch }) {
  const { t } = useLang();
  const [val, setVal] = useState("");
  const credit = tx.amount > 0;
  const opts = credit ? invoices.filter((i) => i.status !== "paid").map((i) => ({ id: i.id, label: `${i.number} · ${i.client_name} · ${i.total.toFixed(2)}` }))
    : expenses.map((e) => ({ id: e.id, label: `${e.vendor} · ${e.description} · ${e.total.toFixed(2)}` }));
  return (
    <div className="flex items-center gap-1">
      <NativeSelect data-testid={`bank-match-select-${tx.id}`} value={val} onChange={(e) => setVal(e.target.value)} className="h-8 max-w-[260px] text-xs">
        <option value="">{t(credit ? "pickInvoice" : "pickExpense")}</option>
        {(tx.suggestions || []).map((s) => <option key={`s-${s.id}`} value={s.id}>★ {s.label}</option>)}
        {opts.filter((o) => !(tx.suggestions || []).some((s) => s.id === o.id)).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </NativeSelect>
      <Button size="sm" variant="outline" data-testid={`bank-match-btn-${tx.id}`} disabled={!val} onClick={() => onMatch(tx, credit ? "invoice" : "expense", val)}><Link2 className="h-3.5 w-3.5" /></Button>
    </div>
  );
}

export default function Bank() {
  const { t, locale } = useLang();
  const [rows, setRows] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const load = () => Promise.all([api.get("/bank/transactions"), api.get("/invoices"), api.get("/expenses")]).then(([a, b, c]) => { setRows(a.data); setInvoices(b.data); setExpenses(c.data); });
  useEffect(() => { load(); }, []);

  const importFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const { data } = await api.post("/bank/import", { csv: await file.text() });
      toast.success(t("bankImported").replace("{added}", data.added).replace("{matched}", data.matched).replace("{skipped}", data.skipped));
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };
  const act = async (fn) => { try { await fn(); load(); } catch (e) { toast.error(errMsg(e)); } };
  const onMatch = (tx, type, target_id) => act(() => api.post(`/bank/transactions/${tx.id}/match`, { type, target_id }));
  const shown = rows.filter((r) => filter === "all" || r.status === filter);
  const counts = rows.reduce((a, r) => ({ ...a, [r.status]: (a[r.status] || 0) + 1 }), {});

  return (
    <div data-testid="bank-page">
      <PageHeader title={t("bank")} subtitle={t("bankSubtitle")}
        actions={<div className="flex gap-2">
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" data-testid="bank-file-input" onChange={(e) => importFile(e.target.files?.[0])} />
          <Button data-testid="bank-import-btn" disabled={busy} onClick={() => fileRef.current?.click()} className="bg-[#007A3D] hover:bg-[#005E2E]">{busy ? <Loader2 className="me-1 h-4 w-4 animate-spin" /> : <Upload className="me-1 h-4 w-4" />}{t("importCsv")}</Button>
          {rows.some((r) => r.status !== "matched") && <Button variant="outline" data-testid="bank-clear-btn" onClick={() => window.confirm(t("confirmDelete")) && act(() => api.delete("/bank/transactions"))}><Trash2 className="me-1 h-4 w-4" />{t("clearUnmatched")}</Button>}
        </div>} />
      <Panel className="mb-4 p-4 text-sm text-slate-600">{t("bankHint")}</Panel>
      <div className="mb-4 flex gap-1">
        {["all", "unmatched", "matched", "ignored"].map((f) => (
          <button key={f} data-testid={`bank-filter-${f}`} onClick={() => setFilter(f)} className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${filter === f ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-200/60"}`}>
            {t(`bank_${f}`)} {f !== "all" && counts[f] ? `(${counts[f]})` : ""}
          </button>
        ))}
      </div>
      <Panel className="overflow-x-auto">
        <table className="data-table">
          <thead><tr><th>{t("date")}</th><th>{t("description")}</th><th className="text-end">{t("amount")}</th><th>{t("status")}</th><th>{t("matchedWith")}</th><th className="text-end">{t("actions")}</th></tr></thead>
          <tbody>
            {shown.length === 0 && <EmptyRow cols={6} />}
            {shown.map((tx) => (
              <tr key={tx.id} data-testid={`bank-row-${tx.id}`}>
                <td className="font-mono text-slate-500">{tx.date}</td>
                <td className="max-w-xs truncate">{tx.description}</td>
                <td className={`text-end font-mono font-semibold ${tx.amount > 0 ? "text-[#007A3D]" : "text-[#A50E1F]"}`}>{tx.amount > 0 ? "+" : ""}{tx.amount.toLocaleString(locale, { minimumFractionDigits: 2 })}</td>
                <td><span data-testid={`bank-status-${tx.id}`} className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ${BADGE[tx.status]}`}>{t(`bank_${tx.status}`)}{tx.auto && " · auto"}</span></td>
                <td className="text-sm">{tx.status === "matched" ? <span data-testid={`bank-match-label-${tx.id}`}>{tx.match_label}</span> : tx.status === "unmatched" ? <MatchPicker tx={tx} invoices={invoices} expenses={expenses} onMatch={onMatch} /> : "—"}</td>
                <td className="whitespace-nowrap text-end">
                  {tx.status === "matched" && <button data-testid={`bank-unmatch-${tx.id}`} title={t("unmatch")} onClick={() => act(() => api.post(`/bank/transactions/${tx.id}/unmatch`))} className="rounded p-1.5 text-slate-500 hover:bg-amber-50 hover:text-amber-700"><Link2Off className="h-4 w-4" /></button>}
                  {tx.status === "unmatched" && <button data-testid={`bank-ignore-${tx.id}`} title={t("ignore")} onClick={() => act(() => api.post(`/bank/transactions/${tx.id}/ignore`))} className="rounded p-1.5 text-slate-500 hover:bg-slate-100"><EyeOff className="h-4 w-4" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}
